import './Header.scss';
import { useEffect, useRef, useState } from 'react';
import { Moon, Sun } from 'lucide-react';
import { FaLinkedin, FaGithub, FaWhatsapp, FaEnvelope } from 'react-icons/fa';

const NAV_ITEMS = [
  { label: 'Início',      ref: 'homeRef' },
  { label: 'Sobre',       ref: 'sobreRef' },
  { label: 'Habilidades', ref: 'habilidadesRef' },
  { label: 'Projetos',    ref: 'projetosRef' },
  { label: 'Contato',     ref: 'contatoRef' },
];

const SOCIAL_LINKS = [
  { href: 'https://www.linkedin.com/in/danilo-braga-785b70136/', icon: <FaLinkedin />, label: 'LinkedIn',  cls: 'linkedin' },
  { href: 'https://github.com/devdanilobraga',                   icon: <FaGithub />,   label: 'GitHub',    cls: 'github' },
  { href: 'https://wa.me/5521971536909',                         icon: <FaWhatsapp />, label: 'WhatsApp',  cls: 'whatsapp' },
  { href: 'mailto:danilobragam@gmail.com',                       icon: <FaEnvelope />, label: 'E-mail',    cls: 'email' },
];

function SocialIcons({ variant }) {
  return (
    <div className={`social-icons ${variant}`} aria-label="Redes sociais">
      {SOCIAL_LINKS.map((s) => (
        <a
          key={s.cls}
          href={s.href}
          target="_blank"
          rel="noopener noreferrer"
          className={`icon-link ${s.cls}`}
          aria-label={s.label}
        >
          {s.icon}
        </a>
      ))}
    </div>
  );
}

function getInitialTheme() {
  try {
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
  } catch { /* storage indisponível */ }
  // Troque por "return false;" se quiser que o padrão seja sempre o tema claro
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

function Header({ scrollToSection, homeRef, sobreRef, habilidadesRef, projetosRef, contatoRef }) {
  const refs = { homeRef, sobreRef, habilidadesRef, projetosRef, contatoRef };

  const [menuOpen, setMenuOpen]   = useState(false);
  const [darkMode, setDarkMode]   = useState(getInitialTheme);
  const [scrolled, setScrolled]   = useState(false);
  const [hidden, setHidden]       = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  const progressRef = useRef(null);
  const lockRef     = useRef(0); // trava observer/auto-hide durante o scroll de um clique

  // Tema (persistido)
  useEffect(() => {
    document.body.classList.toggle('dark-theme', darkMode);
    try { localStorage.setItem('theme', darkMode ? 'dark' : 'light'); } catch { /* ok */ }
  }, [darkMode]);

  // Trava o scroll do body com o drawer aberto
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // ESC fecha o drawer
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  // Fecha o drawer ao virar desktop (evita body travado)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = (e) => { if (e.matches) setMenuOpen(false); };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Scroll: estado "scrolled", auto-hide e barra de progresso (1 handler, throttle por rAF)
  useEffect(() => {
    let lastY = window.scrollY;
    let ticking = false;

    const update = () => {
      const y = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;

      setScrolled(y > 40);

      if (progressRef.current) {
        progressRef.current.style.transform = `scaleX(${max > 0 ? Math.min(y / max, 1) : 0})`;
      }

      if (Date.now() > lockRef.current) {
        if (y > lastY + 6 && y > 240) setHidden(true);
        else if (y < lastY - 6 || y <= 240) setHidden(false);
      }

      lastY = y;
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  // Seção ativa
  useEffect(() => {
    const refValues = NAV_ITEMS.map((item) => refs[item.ref]?.current).filter(Boolean);
    if (!refValues.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (Date.now() < lockRef.current) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = refValues.indexOf(entry.target);
            if (idx !== -1) setActiveIdx(idx);
          }
        });
      },
      { rootMargin: '-40% 0px -55% 0px' }
    );

    refValues.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [homeRef, sobreRef, habilidadesRef, projetosRef, contatoRef]);

  const goTo = (refKey, idx) => {
    lockRef.current = Date.now() + 1100;
    setActiveIdx(idx);
    setHidden(false);
    scrollToSection(refs[refKey]);
    setMenuOpen(false);
  };

  const classes = [
    'header',
    scrolled ? 'scrolled' : '',
    hidden && !menuOpen ? 'hidden' : '',
  ].filter(Boolean).join(' ');

  return (
    // Foco por teclado sempre revela o header
    <header className={classes} onFocusCapture={() => setHidden(false)}>
      <div className="container">

        {/* Logo */}
        <button
  className="logo"
  onClick={() => window.location.reload()}
  aria-label="Atualizar página"
>
  <span className="logo-initials">DB</span>
  <span className="logo-dot" aria-hidden="true" />
</button>

        {/* Nav (drawer no mobile, inline no desktop) */}
        <nav id="primary-nav" className={`nav ${menuOpen ? 'open' : ''}`} aria-label="Navegação principal">
          <ul>
            {NAV_ITEMS.map((item, i) => (
              <li key={item.ref} style={{ '--i': i }}>
                <button
                  className={activeIdx === i ? 'active' : ''}
                  aria-current={activeIdx === i ? 'location' : undefined}
                  onClick={() => goTo(item.ref, i)}
                >
                  <span className="nav-index" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
                  {item.label}
                </button>
              </li>
            ))}
          </ul>

          <SocialIcons variant="mobile-only" />
        </nav>

        {/* Ações do lado direito */}
        <div className="header-actions">
          <SocialIcons variant="desktop-only" />

          <button
            className="theme-toggle"
            onClick={() => setDarkMode((d) => !d)}
            aria-label={darkMode ? 'Ativar modo claro' : 'Ativar modo escuro'}
          >
            {darkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          <button
            className={`menu-toggle ${menuOpen ? 'open' : ''}`}
            onClick={() => setMenuOpen((o) => !o)}
            aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={menuOpen}
            aria-controls="primary-nav"
          >
            <span className="bar" />
            <span className="bar" />
            <span className="bar" />
          </button>
        </div>
      </div>

      {/* Barra de progresso de leitura */}
      <div className="progress" ref={progressRef} aria-hidden="true" />

      {menuOpen && (
        <div className="overlay" onClick={() => setMenuOpen(false)} aria-hidden="true" />
      )}
    </header>
  );
}

export default Header;